import React, { ReactNode, useEffect, useState } from 'react';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { createFeatureManager } from '@justeat/feature-management';
import { JetFMContextProvider, FMSource } from './JetFMProvider';
import { v4 as uuidv4 } from 'uuid';
import { Logger } from '@justeat/feature-management/src/logger';

interface componentProps {
  children: ReactNode;
}

interface IGenerateJetFMSettings {
  country: string | undefined;
  scopeId: string;
  scopeKey: string;
  source: FMSource;
}

const generateJetFMSettings = ({
  country = '',
  scopeId,
  scopeKey,
  source,
}: IGenerateJetFMSettings) => {
  const environment =
    process.env.NODE_ENV === 'production'
      ? 'production'
      : source === 'MCA'
      ? 'staging'
      : 'development';
  const loggerFunct: Logger = {
    logError: (message) => {
      console.error(message);
    },
    logInfo: (_message) => {},
    logWarn: (message) => {
      console.warn(message);
    },
  };

  return {
    keyPrefix: scopeId,
    cdn: {
      host: 'https://features.api.justeattakeaway.com',
      scope: scopeId,
      environment,
      key: scopeKey,
      poll: true,
      pollInterval: 1000 * 60,
    },
    getContext: () => ({
      restrictionsProviders: new Map()
        .set('Country', () => country.toUpperCase())
        .set('Platform', () => 'Web'),
      anonUserId: uuidv4(),
    }),
    onTrack: () => {},
    logger: loggerFunct,
  };
};

const JetFMWrapper = ({ children }: componentProps) => {
  const { restaurant } = useKeycloakUserState();
  const [featureManagement, setFeatureManagement] = useState<Partial<Record<FMSource, Object>>>({});

  const loadJETFM = async () => {
    const country = restaurant?.country;

    const [fmMCP, fmMCA] = await Promise.all([
      (async () => {
        const fm = createFeatureManager(
          generateJetFMSettings({
            country,
            scopeId: JETFM_MCP.SCOPE_ID,
            scopeKey: JETFM_MCP.SCOPE_KEY,
            source: 'MCP',
          })
        );
        await fm.loadFromCdn();
        return fm;
      })(),
      (async () => {
        const fm = createFeatureManager(
          generateJetFMSettings({
            country,
            scopeId: JETFM_MCA.SCOPE_ID,
            scopeKey: JETFM_MCA.SCOPE_KEY,
            source: 'MCA',
          })
        );
        await fm.loadFromCdn();
        return fm;
      })(),
    ]);

    setFeatureManagement({ MCP: fmMCP, MCA: fmMCA });
  };

  useEffect(() => {
    loadJETFM();
  }, [restaurant?.country]);

  return <JetFMContextProvider value={{ featureManagement }}>{children}</JetFMContextProvider>;
};

export default JetFMWrapper;
